/**
 * File: AttentionList.tsx
 * Path: apps/admin/src/shared/components/overview/AttentionList.tsx
 * Description: The "Needs attention" list shared by the accounting Overview
 * and the CRM Today page: slim one-line items, each a link with a status tag.
 * It scrolls on its own. With `fill` it takes the height of the box it sits
 * in (set by the chart beside it) and never makes that box taller; without
 * it, it stops at a fixed height.
 */

import React from "react";
import { Link } from "react-router-dom";
import { cn } from "@idk/utils";
import { Pill, type PillTone } from "../BooksPrimitive";

export interface AttentionLine {
  id: string;
  title: string;
  detail: string;
  tag: string;
  tone: PillTone;
  /** Where opening it goes. */
  to: string;
}

export const AttentionList: React.FC<{
  items: readonly AttentionLine[];
  label: string;
  empty: string;
  fill?: boolean;
}> = ({ items, label, empty, fill = false }) => {
  if (items.length === 0) {
    return <p className="border border-dashed border-border/60 p-gutter-md text-sm text-muted-foreground">{empty}</p>;
  }
  const list = (
    <ul
      className={cn(
        "space-y-stack-xs overflow-y-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        fill ? "h-full" : "max-h-96",
      )}
      aria-label={label}
      tabIndex={0}
    >
      {items.map((item) => (
        <li key={item.id}>
          <Link
            to={item.to}
            title={item.detail}
            className="flex min-h-11 items-center justify-between gap-stack-sm border border-border/50 px-gutter-sm hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span className="min-w-0 truncate text-sm font-bold text-foreground">{item.title}</span>
            <Pill tone={item.tone}>{item.tag}</Pill>
          </Link>
        </li>
      ))}
    </ul>
  );
  if (!fill) return list;
  // On a tablet and up the list is pinned inside a box the chart sizes, so a long list scrolls instead of stretching the row.
  return <div className="h-96 md:h-auto md:min-h-48 md:flex-1 md:relative"><div className="h-full md:absolute md:inset-0">{list}</div></div>;
};
